/**
 * useColor — Reactive exterior color change hook.
 *
 * Watches `selectedColorId` in the store. Whenever it changes, finds the
 * matching ResolvedColorDefinition and calls engine.setMaterial() for each
 * material zone (primary + optional secondary).
 *
 * This hook only handles *changes* after the initial load. The initial color
 * application (without animation) is handled in useEngine's sceneLoaded handler.
 *
 * Separation of concerns:
 *   useEngine    — initial color (no animation, applied once on sceneLoaded)
 *   useColor     — subsequent changes (animated, driven by store)
 *
 * Call site: Viewer.tsx  (has access to both engineRef and config)
 *
 * @see packages/brand-audi/src/hooks/useEngine.ts — initial color
 * @see docs/ims-engine-api/06-feature-color-system.md
 */
import { useEffect, useRef } from 'react';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';

export function useColor(
  engineRef: React.RefObject<Engine | null>,
  config: ResolvedConfig | null,
): void {
  const selectedColorId = useAppStore((s) => s.selectedColorId);
  // Track the prev color id so we skip the first synchronisation pulse
  // that fires when useEngine already applied the color on sceneLoaded.
  const prevColorIdRef  = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // Skip the very first render (undefined → first value) to avoid
    // double-applying the initial color that useEngine already set.
    if (prevColorIdRef.current === undefined) {
      prevColorIdRef.current = selectedColorId;
      return;
    }

    // No-op if color didn't actually change
    if (selectedColorId === prevColorIdRef.current) return;
    prevColorIdRef.current = selectedColorId;

    if (!selectedColorId || !config) return;
    const engine = engineRef.current;
    if (!engine) return;

    const colorDef = config.colors.find((c) => c.id === selectedColorId);
    if (!colorDef) return;

    // Apply primary zone (body color)
    colorDef.primary.materials.forEach((mat) => {
      engine.setMaterial(mat, {
        preset:    'car-paint',
        colorCode: colorDef.primary.colorCode,
        type:      colorDef.primary.type,
        animated:  true,   // smooth lerp transition (900ms default)
      });
    });

    // Apply secondary zone (e.g. contrast roof) if present
    if (colorDef.secondary) {
      colorDef.secondary.materials.forEach((mat) => {
        engine.setMaterial(mat, {
          preset:    'car-paint',
          colorCode: colorDef.secondary!.colorCode,
          type:      colorDef.secondary!.type,
          animated:  true,
        });
      });
    }
  }, [selectedColorId, engineRef, config]);
}
